/**
 * APEX v8.1 — useRankBenchmarks
 *
 * Charge les benchmarks ADC depuis la table Supabase `rank_benchmarks`.
 * Convertit les rows DB au format attendu par computeApexScore()
 * (objet { IRON: { cs_per_min: {p25,p50,p75}, ... }, BRONZE: ... }).
 *
 * Fallback automatique sur TIER_BENCHMARKS_FALLBACK si :
 *   - la table n'existe pas encore (avant migration)
 *   - erreur réseau
 *   - pas de données pour le tier demandé
 */

import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { TIER_BENCHMARKS_FALLBACK } from '../lib/apexScore';

// Convertit une row DB → format { cs_per_min: {p25,p50,p75}, kda: {...}, ... }
function rowToApexFormat(row) {
  return {
    cs_per_min:     { p25: Number(row.cs_p25),      p50: Number(row.cs_p50),      p75: Number(row.cs_p75) },
    kda:            { p25: Number(row.kda_p25),     p50: Number(row.kda_p50),     p75: Number(row.kda_p75) },
    lane_wr_15:     { p25: Number(row.lane_wr_p25), p50: Number(row.lane_wr_p50), p75: Number(row.lane_wr_p75) },
    damage_per_min: { p25: Number(row.dmg_p25),     p50: Number(row.dmg_p50),     p75: Number(row.dmg_p75) },
    vision:         { p25: Number(row.vision_p25),  p50: Number(row.vision_p50),  p75: Number(row.vision_p75) },
    deaths_per_game:{ p25: Number(row.deaths_p25),  p50: Number(row.deaths_p50),  p75: Number(row.deaths_p75) },
    win_rate:       { p25: Number(row.wr_p25),      p50: Number(row.wr_p50),      p75: Number(row.wr_p75) },
  };
}

export function useRankBenchmarks(role = 'ADC') {
  const [benchmarks, setBenchmarks] = useState(TIER_BENCHMARKS_FALLBACK);
  const [isFallback, setIsFallback]   = useState(true);
  const [loading, setLoading]         = useState(true);
  const [patch, setPatch]             = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('rank_benchmarks')
          .select('*')
          .eq('role', role);

        if (cancelled) return;

        if (error || !data || data.length === 0) {
          setBenchmarks(TIER_BENCHMARKS_FALLBACK);
          setIsFallback(true);
          return;
        }

        const result = {};
        for (const row of data) {
          result[row.tier] = rowToApexFormat(row);
        }

        const required = ['IRON', 'GOLD', 'DIAMOND', 'MASTER'];
        const hasAll = required.every(t => result[t]);
        if (!hasAll) {
          setBenchmarks(TIER_BENCHMARKS_FALLBACK);
          setIsFallback(true);
          return;
        }

        setBenchmarks(result);
        setIsFallback(false);
        setPatch(data[0]?.patch ?? null);
      } catch (err) {
        if (!cancelled) {
          setBenchmarks(TIER_BENCHMARKS_FALLBACK);
          setIsFallback(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [role]);

  return { benchmarks, isFallback, loading, patch };
}
